import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import {
  signOut, getAllOrganizations, getContactMessages,
  getPlanPricing, updatePlanPrice,
  superadminUpgradeOrg, getAdminActions
} from '../lib/supabase'

function ViewOnlyBanner() {
  return (
    <div className="alert alert-warning mb-3" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      🔒 View only — changes on this tab are not permitted
    </div>
  )
}

// Security confirmation modal — requires typing a phrase to confirm destructive actions
function ConfirmActionModal({ isOpen, action, org, onConfirm, onClose }) {
  const [phrase, setPhrase] = useState('')
  const [reason, setReason] = useState('')
  const required = `confirm ${org?.name?.toLowerCase().replace(/\s+/g, '-') || 'action'}`

  useEffect(() => { setPhrase(''); setReason('') }, [isOpen])

  if (!isOpen) return null
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">⚠️ Confirm superadmin action</div>
        <div className="modal-sub mb-3">This action is logged permanently and cannot be undone.</div>
        <div className="alert alert-error mb-3" style={{ fontSize: 13 }}>
          <strong>Action:</strong> {action?.label}<br />
          <strong>Organisation:</strong> {org?.name}<br />
          <strong>Current plan:</strong> {org?.plan} · {org?.subscription_status}
        </div>
        <div className="form-group">
          <label className="field-label">Reason for this change *</label>
          <textarea placeholder="e.g. Admin requested upgrade via WhatsApp, payment confirmed via Paystack ref CF_..." value={reason} onChange={e => setReason(e.target.value)} style={{ minHeight: 70 }} />
          <div className="field-hint">This is stored in the audit log. Be specific.</div>
        </div>
        <div className="form-group">
          <label className="field-label">Type <strong style={{ color: 'var(--red)', fontFamily: 'var(--mono)' }}>{required}</strong> to confirm</label>
          <input type="text" placeholder={required} value={phrase} onChange={e => setPhrase(e.target.value)} />
        </div>
        <div className="flex gap-2 mt-3">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-danger" style={{ flex: 1 }}
            disabled={phrase !== required || !reason.trim()}
            onClick={() => onConfirm(reason)}
          >
            Confirm action
          </button>
        </div>
      </div>
    </div>
  )
}

export default function SuperAdmin() {
  const { user, profile, isSuperAdmin, loading } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState('overview')
  const [orgs, setOrgs] = useState([])
  const [contacts, setContacts] = useState([])
  const [planPricing, setPlanPricing] = useState([])
  const [editedPrices, setEditedPrices] = useState({})
  const [pricingSaving, setPricingSaving] = useState(false)
  const [pricingSaved, setPricingSaved] = useState(false)
  const [auditLog, setAuditLog] = useState([])
  const [dataLoading, setDataLoading] = useState(false)
  const [orgSearch, setOrgSearch] = useState('')
  const [confirmModal, setConfirmModal] = useState(null) // { action, org }
  const [actionLoading, setActionLoading] = useState(false)
  const [actionMsg, setActionMsg] = useState('')

  useEffect(() => {
    if (!loading && user !== undefined && profile !== undefined) {
      if (!user || !isSuperAdmin) navigate('/login')
    }
  }, [loading, user, profile, isSuperAdmin, navigate])

  useEffect(() => {
    if (!isSuperAdmin) return
    setDataLoading(true)
    Promise.all([
      getAllOrganizations(),
      getContactMessages(),
      getPlanPricing(),
      getAdminActions(),
    ]).then(([o, c, pricing, actions]) => {
      setOrgs(o.data || [])
      setContacts(c.data || [])
      const pd = pricing.data || []
      setPlanPricing(pd)
      setEditedPrices(Object.fromEntries(pd.map(p => [p.plan_key, p.price])))
      setAuditLog(actions.data || [])
      setDataLoading(false)
    })
  }, [isSuperAdmin])

  if (loading || (user && dataLoading)) return (
    <div className="page-center"><span className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} /></div>
  )

  const activeOrgs = orgs.filter(o => o.subscription_status === 'active')
  const trialOrgs = orgs.filter(o => o.subscription_status === 'trial')
  const expiredOrgs = orgs.filter(o => o.subscription_status === 'expired' || o.subscription_status === 'read_only')
  const unreadContacts = contacts.filter(c => !c.replied)
  const planPrices = Object.fromEntries(planPricing.map(p => [p.plan_key, p.price]))
  const mrr = activeOrgs.reduce((sum, o) => sum + (planPrices[o.plan] || 0), 0)

  const expiringSoon = orgs.filter(o => {
    const d = o.trial_ends_at || o.semester_end
    if (!d) return false
    const days = Math.ceil((new Date(d) - new Date()) / 86400000)
    return days > 0 && days <= 7
  })

  const filteredOrgs = orgs.filter(o =>
    !orgSearch ||
    o.name?.toLowerCase().includes(orgSearch.toLowerCase()) ||
    o.short_code?.toLowerCase().includes(orgSearch.toLowerCase()) ||
    o.plan?.toLowerCase().includes(orgSearch.toLowerCase()) ||
    o.subscription_status?.toLowerCase().includes(orgSearch.toLowerCase())
  )

  // ── Execute a confirmed superadmin action ─────────────────────
  async function executeAction(reason) {
    if (!confirmModal) return
    setActionLoading(true); setActionMsg('')
    const { action, org } = confirmModal
    const updates = action.updates(org)

    const { error } = await superadminUpgradeOrg(org.id, updates, reason, user.id)
    if (error) {
      setActionMsg('Error: ' + error.message)
    } else {
      setActionMsg(`✓ ${action.label} applied to ${org.name}`)
      // Refresh org list
      const { data } = await getAllOrganizations()
      setOrgs(data || [])
      // Refresh audit log
      const { data: actions } = await getAdminActions()
      setAuditLog(actions || [])
    }
    setActionLoading(false)
    setConfirmModal(null)
  }

  // Available actions per org
  function getActions(org) {
    const actions = []
    if (org.plan === 'starter') {
      actions.push({
        label: 'Upgrade to Pro',
        updates: () => ({ plan: 'pro', subscription_status: 'active' }),
        danger: false,
      })
    }
    if (org.subscription_status !== 'active') {
      actions.push({
        label: 'Activate (mark as paid)',
        updates: () => ({ subscription_status: 'active' }),
        danger: false,
      })
    }
    if (org.subscription_status === 'active') {
      actions.push({
        label: 'Extend by 30 days',
        updates: (o) => {
          const end = new Date(o.semester_end || new Date())
          end.setDate(end.getDate() + 30)
          return { semester_end: end.toISOString().slice(0, 10) }
        },
        danger: false,
      })
    }
    if (org.subscription_status !== 'trial') {
      actions.push({
        label: 'Reset to trial (14 days)',
        updates: () => ({
          subscription_status: 'trial',
          trial_ends_at: new Date(Date.now() + 14 * 86400000).toISOString(),
        }),
        danger: true,
      })
    }
    actions.push({
      label: 'Suspend org',
      updates: () => ({ subscription_status: 'expired' }),
      danger: true,
    })
    return actions
  }

  return (
    <div className="page">
      <nav className="topnav">
        <div className="flex items-center gap-2">
          <div className="logo">Campus<span>Flow</span></div>
          <span style={{ fontSize: 11, background: 'var(--red-bg)', color: 'var(--red)', border: '1px solid #fecaca', borderRadius: 99, padding: '2px 8px', fontWeight: 700, letterSpacing: '0.04em' }}>SUPERADMIN</span>
        </div>
        <div className="flex items-center gap-2">
          {unreadContacts.length > 0 && <span className="badge badge-red">{unreadContacts.length} messages</span>}
          <span className="text-xs text-muted">{profile?.full_name}</span>
          <button className="btn btn-ghost btn-sm" onClick={async () => { await signOut(); navigate('/') }}>Sign out</button>
        </div>
      </nav>

      <div className="container" style={{ paddingTop: '1.5rem', paddingBottom: '3rem' }}>

        {actionMsg && (
          <div className={`alert ${actionMsg.startsWith('✓') ? 'alert-success' : 'alert-error'} mb-3`}>{actionMsg}</div>
        )}

        <div className="tab-bar">
          {[
            { key: 'overview',       label: 'Overview' },
            { key: 'organisations',  label: `Orgs (${orgs.length})` },
            { key: 'manage',         label: '⚙️ Manage orgs' },
            { key: 'contacts',       label: `Messages${unreadContacts.length > 0 ? ` (${unreadContacts.length})` : ''}` },
            { key: 'pricing',        label: '💰 Pricing' },
            { key: 'audit',          label: '🔐 Audit log' },
          ].map(t => (
            <button key={t.key} className={`tab-btn ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
          ))}
        </div>

        {/* ── Overview ── */}
        {tab === 'overview' && (
          <div className="fade-up">
            <ViewOnlyBanner />
            <div className="metric-grid">
              <div className="metric"><div className="metric-val" style={{ color: 'var(--green)' }}>GH₵{mrr.toLocaleString()}</div><div className="metric-lbl">Revenue this year</div></div>
              <div className="metric"><div className="metric-val">{activeOrgs.length}</div><div className="metric-lbl">Active orgs</div></div>
              <div className="metric"><div className="metric-val">{trialOrgs.length}</div><div className="metric-lbl">On trial</div></div>
              <div className="metric"><div className="metric-val" style={{ color: expiredOrgs.length > 0 ? 'var(--red)' : undefined }}>{expiredOrgs.length}</div><div className="metric-lbl">Expired</div></div>
            </div>
            {expiringSoon.length > 0 && (
              <div className="card" style={{ borderLeft: '3px solid var(--amber)' }}>
                <div className="card-title mb-2">⚠️ Expiring within 7 days</div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Org</th><th>Plan</th><th>Status</th><th>Expires</th></tr></thead>
                    <tbody>
                      {expiringSoon.map(o => {
                        const d = o.trial_ends_at || o.semester_end
                        const days = Math.ceil((new Date(d) - new Date()) / 86400000)
                        return (
                          <tr key={o.id}>
                            <td style={{ fontWeight: 500 }}>{o.name}</td>
                            <td><span className="badge badge-blue">{o.plan}</span></td>
                            <td><span className="badge badge-amber">{o.subscription_status}</span></td>
                            <td><span className="badge badge-red">{days}d</span></td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── All orgs (view only) ── */}
        {tab === 'organisations' && (
          <div className="fade-up">
            <ViewOnlyBanner />
            <div className="flex justify-between items-center mb-3">
              <h2 style={{ fontSize: 16, fontWeight: 600 }}>All organisations</h2>
              <input type="text" placeholder="Search..." value={orgSearch} onChange={e => setOrgSearch(e.target.value)} style={{ width: 200 }} />
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Type</th><th>Plan</th><th>Status</th><th>Code</th><th>Registered</th></tr></thead>
                <tbody>
                  {filteredOrgs.map(o => (
                    <tr key={o.id}>
                      <td style={{ fontWeight: 500 }}>{o.name}</td>
                      <td className="text-sm text-muted">{o.org_type}</td>
                      <td><span className="badge badge-blue">{o.plan}</span></td>
                      <td><span className={`badge ${o.subscription_status === 'active' ? 'badge-green' : o.subscription_status === 'trial' ? 'badge-blue' : 'badge-red'}`}>{o.subscription_status}</span></td>
                      <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--blue)', fontWeight: 600 }}>{o.short_code}</td>
                      <td className="text-sm text-muted">{new Date(o.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Manage orgs (with security gates) ── */}
        {tab === 'manage' && (
          <div className="fade-up">
            <div className="alert alert-error mb-3" style={{ fontSize: 13 }}>
              🔐 <strong>Every action here is logged</strong> with your user ID, a required reason, and a confirmation phrase. There are no silent changes.
            </div>
            <div className="flex justify-between items-center mb-3">
              <h2 style={{ fontSize: 16, fontWeight: 600 }}>Manage organisations</h2>
              <input type="text" placeholder="Search..." value={orgSearch} onChange={e => setOrgSearch(e.target.value)} style={{ width: 200 }} />
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              {filteredOrgs.map(o => (
                <div key={o.id} className="card">
                  <div className="flex justify-between items-start" style={{ flexWrap: 'wrap', gap: 10 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{o.name}</div>
                      <div className="flex gap-2 items-center">
                        <span className="badge badge-blue">{o.plan}</span>
                        <span className={`badge ${o.subscription_status === 'active' ? 'badge-green' : o.subscription_status === 'trial' ? 'badge-amber' : 'badge-red'}`}>{o.subscription_status}</span>
                        <span className="text-xs text-muted">{o.org_type}</span>
                      </div>
                      {o.trial_ends_at && o.subscription_status === 'trial' && (
                        <div className="text-xs text-muted mt-1">
                          Trial ends: {new Date(o.trial_ends_at).toLocaleDateString()}
                          {' '}({Math.ceil((new Date(o.trial_ends_at) - new Date()) / 86400000)} days)
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                      {getActions(o).map((action, i) => (
                        <button
                          key={i}
                          className={`btn btn-sm ${action.danger ? 'btn-danger' : 'btn-secondary'}`}
                          onClick={() => setConfirmModal({ action, org: o })}
                          disabled={actionLoading}
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Messages ── */}
        {tab === 'contacts' && (
          <div className="fade-up">
            <ViewOnlyBanner />
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: '0.25rem' }}>Contact messages</h2>
            <p className="text-sm text-muted mb-3">{unreadContacts.length} unread · {contacts.length} total</p>
            {contacts.length === 0 ? (
              <div className="card"><div className="empty-state"><div className="empty-state-title">No messages yet</div></div></div>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {contacts.map(c => (
                  <div key={c.id} className="card" style={{ borderLeft: `3px solid ${c.replied ? 'var(--green)' : 'var(--blue)'}` }}>
                    <div className="flex justify-between items-start">
                      <div style={{ flex: 1 }}>
                        <div className="flex gap-2 items-center mb-1">
                          <span style={{ fontWeight: 600 }}>{c.name}</span>
                          <span style={{ fontSize: 12, color: 'var(--blue)', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigator.clipboard.writeText(c.email)}>{c.email}</span>
                          {!c.replied && <span className="badge badge-blue">New</span>}
                        </div>
                        <div style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 4 }}>{c.message}</div>
                        <div className="text-xs text-muted">{new Date(c.created_at).toLocaleString()}</div>
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={() => navigator.clipboard.writeText(c.email)}>Copy email</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Pricing (EDITABLE) ── */}
        {tab === 'pricing' && (
          <div className="fade-up">
            <div className="alert alert-success mb-3" style={{ fontSize: 13 }}>✏️ Only this tab allows changes — price updates are immediate for new signups</div>
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: '0.25rem' }}>Plan pricing</h2>
            <p className="text-sm text-muted mb-3">Billed yearly. Existing subscriptions unaffected.</p>
            <div className="card">
              {planPricing.map(p => (
                <div key={p.plan_key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
                  <span className="badge badge-blue" style={{ minWidth: 70, justifyContent: 'center', textTransform: 'capitalize' }}>{p.plan_key}</span>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>GH₵</span>
                  <input type="number" min="0" step="50"
                    value={editedPrices[p.plan_key] ?? p.price}
                    onChange={e => setEditedPrices(prev => ({ ...prev, [p.plan_key]: parseInt(e.target.value) || 0 }))}
                    style={{ width: 120, fontWeight: 700, fontSize: 18, textAlign: 'right' }} />
                  <span className="text-xs text-muted">/ year</span>
                </div>
              ))}
              <div className="flex gap-2 mt-4">
                <button className="btn btn-primary" disabled={pricingSaving} onClick={async () => {
                  setPricingSaving(true); setPricingSaved(false)
                  await Promise.all(Object.entries(editedPrices).map(([key, price]) => updatePlanPrice(key, price)))
                  const { data } = await getPlanPricing()
                  if (data) setPlanPricing(data)
                  setPricingSaving(false); setPricingSaved(true)
                  setTimeout(() => setPricingSaved(false), 3000)
                }}>
                  {pricingSaving ? <><span className="spinner" /> Saving...</> : 'Save prices'}
                </button>
                {pricingSaved && <span className="alert alert-success" style={{ padding: '6px 14px', margin: 0 }}>✓ Saved</span>}
              </div>
              <p className="field-hint mt-2">Active org revenue: <strong>GH₵{mrr.toLocaleString()}</strong></p>
            </div>
          </div>
        )}

        {/* ── Audit log ── */}
        {tab === 'audit' && (
          <div className="fade-up">
            <ViewOnlyBanner />
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: '0.25rem' }}>Superadmin audit log</h2>
            <p className="text-sm text-muted mb-3">Every action taken from this panel, permanently recorded.</p>
            {auditLog.length === 0 ? (
              <div className="card"><div className="empty-state"><div className="empty-state-title">No actions recorded yet</div></div></div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Action</th><th>Organisation</th><th>Change</th><th>Reason</th><th>Date</th></tr></thead>
                  <tbody>
                    {auditLog.map(a => (
                      <tr key={a.id}>
                        <td><span className="badge badge-gray">{a.action?.replace(/_/g, ' ')}</span></td>
                        <td style={{ fontWeight: 500 }}>{a.organizations?.name || '—'}</td>
                        <td style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text-muted)' }}>
                          {a.new_value ? JSON.stringify(a.new_value).slice(0, 60) : '—'}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 200 }}>{a.reason || '—'}</td>
                        <td className="text-xs text-muted">{new Date(a.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Security confirmation modal ── */}
      <ConfirmActionModal
        isOpen={!!confirmModal}
        action={confirmModal?.action}
        org={confirmModal?.org}
        onConfirm={executeAction}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  )
}
